import React from 'react';
import { AlertTriangle, Home, RefreshCw, Search, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const NotFoundPage = () => {
  // For actual implementation, you'll need to import useNavigate from react-router-dom
  const navigate = useNavigate();
  const location = useLocation();

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '40px',
      maxWidth: '600px',
      width: '100%',
      textAlign: 'center'
    },
    header: {
      marginBottom: '32px'
    },
    icon: {
      width: '64px',
      height: '64px',
      margin: '0 auto 16px',
      color: '#f59e0b'
    },
    errorCode: {
      fontSize: '72px',
      fontWeight: '900',
      color: '#ef4444',
      marginBottom: '8px',
      lineHeight: '1'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '16px',
      color: '#6b7280',
      margin: '0',
      lineHeight: '1.5'
    },
    messageBox: {
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '32px',
      textAlign: 'left'
    },
    messageTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
      fontWeight: '600',
      color: '#92400e'
    },
    messageText: {
      color: '#a16207',
      lineHeight: '1.6',
      margin: '0'
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    button: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      flex: '1',
      minWidth: '140px'
    },
    primaryButton: {
      backgroundColor: '#16a34a'
    },
    secondaryButton: {
      backgroundColor: '#6b7280'
    },
    suggestions: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      textAlign: 'left'
    },
    suggestionsTitle: {
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    suggestionsList: {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    suggestionItem: {
      padding: '8px 0',
      borderBottom: '1px solid #e5e7eb',
      color: '#6b7280',
      fontSize: '14px'
    },
    lastSuggestionItem: {
      borderBottom: 'none'
    },
    footer: {
      fontSize: '14px',
      color: '#9ca3af',
      marginTop: '24px',
      lineHeight: '1.5'
    },
    linkText: {
      color: '#3b82f6',
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.length > 1 ? navigate(-1) : navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.errorCode}>404</div>
          <AlertTriangle style={styles.icon} />
          <h1 style={styles.title}>Page Not Found</h1>
          <p style={styles.subtitle}>
            Sorry, we couldn't find the page you're looking for.
          </p>
        </div>

        <div style={styles.messageBox}>
          <div style={styles.messageTitle}>
            <Search style={{width: '20px', height: '20px'}} />
            What happened?
          </div>
          <p style={styles.messageText}>
            The page you requested doesn't exist or may have been moved. This could happen if:
            you typed the URL incorrectly, followed an outdated link, or the page has been temporarily removed.
          </p>
        </div>

        <div style={styles.buttonContainer}>
          <button
            style={{...styles.button, ...styles.primaryButton}}
            onClick={handleGoHome}
            onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
          >
            <Home style={{width: '20px', height: '20px'}} />
            Go Home
          </button>
          
          <button
            style={{...styles.button, ...styles.secondaryButton}}
            onClick={handleRefresh}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            <RefreshCw style={{width: '20px', height: '20px'}} />
            Refresh
          </button>
        </div>

        <div style={styles.footer}>
          If you believe this is an error, please contact your system administrator.<br />
          <strong>Error Code:</strong> 404 | <strong>Timestamp:</strong> {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;